import React, { useEffect, useState, ChangeEvent } from "react";
import Typography from "@mui/material/Typography";
import {
  Box,
  Container,
  Button,
  CircularProgress,
  MenuItem,
  FormControl,
  Select,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { useSelector } from "react-redux";
import { selectUser } from "../../redux/slices/userSlice";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {
  downloadserverreport,
  getreportsummarydatafrmdb,
} from "../../clients/ReportsService";
import { useInterval } from "../../utils/useInterval";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CountryCodedata from "../../types/CountryCodedata";
import { columnstypes } from "../../types/tablecolumntypes";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { formatDateStr } from "../../utils/FormatDateStr";
import { convertUTCtoLocal } from "../../utils/convertUTCtoLocal";
import Pagination from "../templates/Pagination";

const ReportsScreen: React.FC = () => {
  const user = useSelector(selectUser);
  const defaulttabledata: columnstypes = {
    updatedBy: "",
    runId: null,
    jobId: 0,
    updatedTimeStamp: "",
    reportUser: user.username,
    createdTimeStamp: "",
    tags: "",
    reportStartDate: null,
    reportEndDate: null,
    dockerContainerName: "",
    countryCode: "",
    status: "",
    jobDescription: null,
  };
  const [reportRecords, setReportRecords] = useState<columnstypes[]>([]);
  const [clearTriger, setclearTriger] = useState<boolean>(false);
  const [applybutton, setapplybutton] = useState<boolean>(false);
  const [filterReportRecords, setFilterReportRecords] =
    useState<columnstypes>(defaulttabledata);
  const [loading, setLoading] = useState<boolean>(false);
  const [rows, setRows] = useState("5");
  const [currentPage, setCurrentPage] = useState(1);
  const reportsPerPage = Number(rows);

  const indexOfLastReport = currentPage * reportsPerPage;

  const indexOfFirstReport = indexOfLastReport - reportsPerPage;

  const currentReports: columnstypes[] = reportRecords.slice(
    indexOfFirstReport,
    indexOfLastReport
  );

  const handleChange = (event: any) => {
    setRows(event.target.value);
    setCurrentPage(1);
  };

  const filterdata = async (filterReports: columnstypes) => {
    try {
      const serverReportsResponse = await getreportsummarydatafrmdb(
        user.auth,
        filterReports
      );
      const serverReportsData = await serverReportsResponse.json();
      if (!serverReportsResponse.ok) {
        throw new Error(
          `Server reports request failed with status ${serverReportsResponse.status}`
        );
      }
      if (serverReportsData.reports.length > 0) {
        serverReportsData.reports.sort((a: columnstypes, b: columnstypes) => {
          const dateA = new Date(
            a.reportStartDate != null ? a.reportStartDate : ""
          );
          const dateB = new Date(
            b.reportStartDate != null ? b.reportStartDate : ""
          );
          return dateB.getTime() - dateA.getTime();
        });
        if (reportRecords.length === serverReportsData.reports.length) {
          setCurrentPage(currentPage);
        } else {
          setCurrentPage(1);
        }
        setReportRecords(serverReportsData.reports);
      } else {
        setReportRecords(serverReportsData.reports);
        setCurrentPage(1);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    filterdata({
      ...filterReportRecords,
      reportStartDate: filterReportRecords.reportStartDate
        ? formatDateStr(
            new Date(filterReportRecords.reportStartDate),
            "YYYY-MM-DD HH:mm:ss",
            false
          )
        : null,
      reportEndDate: filterReportRecords.reportEndDate
        ? formatDateStr(
            new Date(filterReportRecords.reportEndDate),
            "YYYY-MM-DD HH:mm:ss",
            false
          )
        : null,
    });
    setCurrentPage(1);
  }, []);

  const handleapplyfilterfn = () => {
    setclearTriger(true);
    filterdata({
      ...filterReportRecords,
      reportStartDate: filterReportRecords.reportStartDate
        ? formatDateStr(
            new Date(filterReportRecords.reportStartDate),
            "YYYY-MM-DD HH:mm:ss",
            false
          )
        : null,
      reportEndDate: filterReportRecords.reportEndDate
        ? formatDateStr(
            new Date(filterReportRecords.reportEndDate),
            "YYYY-MM-DD HH:mm:ss",
            false
          )
        : null,
    });
    setCurrentPage(1);
  };

  const handleremovefilters = () => {
    setclearTriger(!clearTriger);
    setapplybutton(false);
    setFilterReportRecords(defaulttabledata);
    filterdata(defaulttabledata);
  };

  useInterval(
    () => {
      filterdata({
        ...filterReportRecords,
        reportStartDate: filterReportRecords.reportStartDate
          ? formatDateStr(
              new Date(filterReportRecords.reportStartDate),
              "YYYY-MM-DD HH:mm:ss",
              false
            )
          : null,
        reportEndDate: filterReportRecords.reportEndDate
          ? formatDateStr(
              new Date(filterReportRecords.reportEndDate),
              "YYYY-MM-DD HH:mm:ss",
              false
            )
          : null,
      });
    },
    parseInt(process.env.REFRESH_INTERVAL ?? "60000", 10)
  );

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
        }}
      >
        <CircularProgress disableShrink />
      </Box>
    );
  }
  const getReport = async (
    e:
      | React.MouseEvent<HTMLSpanElement>
      | React.MouseEvent<HTMLHeadingElement>
      | null,
    runId: number,
    countrycodeprop: string
  ) => {
    e?.preventDefault();
    setLoading(true);

    try {
      setLoading(false);
      window.open(
        `${process.env.REACT_APP_BASE_URL}/reports/${user.username}/${countrycodeprop}/${runId}/overview-features.html`,
        "_blank"
      );
    } catch (error) {
      // console.error("Error fetching data:", error);
    }
  };

  const downloadreport = async (
    e: React.MouseEvent<HTMLButtonElement>,
    runId: number,
    countrycode: string
  ) => {
    e.preventDefault();
    setLoading(true);
    try {
      const downloadresponse = await downloadserverreport(
        user.auth,
        runId,
        countrycode
      );
      const blob = await downloadresponse.blob();
      if (downloadresponse.ok) {
        setLoading(false);
        toast.success("Downloaded ServerReport: " + runId);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download =
          downloadresponse.headers
            .get("Content-Disposition")
            ?.split("filename=")[1] || `${runId}.zip`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        toast.error(
          "Error Downloading ServerReports" + downloadresponse.statusText
        );
      }
    } catch (error) {
      toast.error("Error Downloading ServerReports" + error);
    }
  };

  return (
    <>
      <Container>
        <Typography
          variant="h5"
          mb={"6px"}
        >
          Reports Summary Details
        </Typography>

        <Box
          sx={{
            display: "flex",
            justifyContent: "left",
            gap: "1rem",
            mt: "6px",
          }}
        >
          <TextField
            placeholder="Enter Job Id"
            size="small"
            id="RunID"
            value={
              filterReportRecords.runId !== null
                ? filterReportRecords.runId
                : ""
            }
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setFilterReportRecords({
                ...filterReportRecords,
                runId: parseInt(e.target.value) || null,
              });

              setclearTriger(true);
              setapplybutton(true);
            }}
            // InputProps={{
            //   endAdornment: (
            //     <SearchIcon
            //       sx={{
            //         cursor: "pointer",
            //       }}
            //     />
            //   ),
            // }}
            sx={{
              width: "21%",
            }}
          />

          <FormControl
            sx={{
              width: "15%",
            }}
          >
            <Select
              required
              size="small"
              id="statuscode"
              value={filterReportRecords.status}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "center",
                "& .MuiSelect-root": {
                  alignSelf: "flex-start",
                },
              }}
              onChange={(e) => {
                setFilterReportRecords({
                  ...filterReportRecords,
                  status: e.target.value,
                });
                setclearTriger(true);
                setapplybutton(true);
              }}
              displayEmpty
              renderValue={(value) =>
                value.length > 0 ? (
                  value
                ) : (
                  <span style={{ color: "#ACAFAE" }}>Status</span>
                )
              }
            >
              {["Completed", "Created", "Inprogress", "Failed"].map((org) => (
                <MenuItem
                  key={org}
                  value={org}
                >
                  {org}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl
            sx={{
              width: "15%",
            }}
          >
            <Select
              required
              id="orgnitioncode"
              size="small"
              value={filterReportRecords.countryCode}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "center",
                "& .MuiSelect-root": {
                  alignSelf: "flex-start",
                },
              }}
              onChange={(e) => {
                setFilterReportRecords({
                  ...filterReportRecords,
                  countryCode: e.target.value,
                });
                setclearTriger(true);
                setapplybutton(true);
              }}
              displayEmpty
              renderValue={(value) =>
                value !== "" ? (
                  value
                ) : (
                  <span style={{ color: "#ACAFAE" }}>Organization</span>
                )
              }
            >
              {CountryCodedata.map((org) => (
                <MenuItem
                  key={org}
                  value={org}
                >
                  {org}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateTimePicker
              slotProps={{
                textField: { placeholder: "Job Start Date" },
              }}
              sx={{
                width: "22%",
                "& .MuiInputBase-root": {
                  height: "40px",
                },
              }}
              ampm={false}
              value={
                filterReportRecords.reportStartDate
                  ? dayjs(filterReportRecords.reportStartDate)
                  : null
              }
              onChange={(date: dayjs.Dayjs | null) => {
                setFilterReportRecords({
                  ...filterReportRecords,
                  reportStartDate: date
                    ? formatDateStr(date.toDate(), "YYYY-MM-DD HH:mm:ss", true)
                    : null,
                });
                setclearTriger(true);
                setapplybutton(true);
              }}
            />
            <DateTimePicker
              slotProps={{
                textField: { placeholder: "Job End Date" },
              }}
              value={
                filterReportRecords.reportEndDate
                  ? dayjs(filterReportRecords.reportEndDate)
                  : null
              }
              sx={{
                width: "22%",
                "& .MuiInputBase-root": {
                  height: "40px",
                },
              }}
              ampm={false}
              onChange={(date: dayjs.Dayjs | null) => {
                setFilterReportRecords({
                  ...filterReportRecords,
                  reportEndDate: date
                    ? formatDateStr(date.toDate(), "YYYY-MM-DD HH:mm:ss", true)
                    : null,
                });
                setclearTriger(true);
                setapplybutton(true);
              }}
            />
          </LocalizationProvider>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            aliginContent: "center",
            justifyContent: "flex-end",
            mt: 1,
            mb: 1,
          }}
        >
          <Button
            size="small"
            disabled={!applybutton}
            onClick={handleapplyfilterfn}
            startIcon={<FilterAltIcon />}
          >
            Apply
          </Button>
          <Button
            size="small"
            disabled={!clearTriger}
            onClick={handleremovefilters}
            sx={{
              ml: 2,
            }}
            startIcon={<FilterAltOffIcon />}
          >
            Clear
          </Button>
        </Box>

        <Paper sx={{ width: "100%" }}>
          <TableContainer
            component={Paper}
            sx={{ mt: "2px", maxHeight: ["328px"] }}
          >
            <Table
              aria-label="Reports table"
              stickyHeader
            >
              <TableHead>
                <TableRow sx={{ fontWeight: "600" }}>
                  <TableCell
                    align="center"
                    sx={{ fontWeight: "600" }}
                  >
                    Job Id
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ fontWeight: "600" }}
                  >
                    Organization
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ fontWeight: "600" }}
                  >
                    Status
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ fontWeight: "600" }}
                  >
                    Start Date
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ fontWeight: "600" }}
                  >
                    End Date
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ fontWeight: "600" }}
                  >
                    Job Description
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ fontWeight: "600" }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              {currentReports.length === 0 && (
                <TableBody
                  sx={{
                    overflowY: "scroll",
                    overflowX: "scroll",
                  }}
                >
                  <TableCell
                    align="center"
                    colSpan={6}
                  >
                    No Reports
                  </TableCell>
                </TableBody>
              )}
              {currentReports.length > 0 && (
                <TableBody sx={{ overflowY: "scroll", overflowX: "scroll" }}>
                  {currentReports.length > 0 &&
                    currentReports.map((report: columnstypes) => (
                      <TableRow
                        key={report.jobId}
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                      >
                        <TableCell align="center">
                          {report.status === "Completed" ? (
                            <a
                              href={`${
                                process.env.REACT_APP_BASE_URL
                              }/reports/${
                                user.username
                              }/${report?.countryCode}/${report?.runId?.toString()}/overview-features.html`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {report.runId !== null ? (
                                <Tooltip
                                  title={report.tags}
                                  placement="top"
                                >
                                  <span>{report.runId}</span>
                                </Tooltip>
                              ) : null}
                            </a>
                          ) : (
                            <>
                              <Tooltip
                                title={report.tags}
                                placement="top"
                              >
                                <span>{report.runId}</span>
                              </Tooltip>
                            </>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {report.countryCode}
                        </TableCell>
                        <TableCell align="left">
                          {report.status.includes("InProgress")
                            ? // <Tooltip
                              //   title={file.status.slice(11, file.status.length)}
                              //   placement="top"
                              // >
                              //   <span>{file.status.split(" ")[0]}</span>
                              // </Tooltip>
                              report.status.split(" ")[0]
                            : report.status}
                        </TableCell>
                        <TableCell align="left">
                          {report.reportStartDate &&
                            formatDateStr(
                              convertUTCtoLocal(report.reportStartDate),
                              "YYYY-MM-DD HH:mm:ss",
                              true
                            )}
                        </TableCell>
                        <TableCell align="left">
                          {report.reportEndDate !== null ? (
                            formatDateStr(
                              convertUTCtoLocal(report.reportEndDate),
                              "YYYY-MM-DD HH:mm:ss",
                              true
                            )
                          ) : (
                            <p>Loading...</p>
                          )}
                        </TableCell>
                        <TableCell
                          align="left"
                          sx={{
                            maxWidth: "200px",
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {report.jobDescription}
                        </TableCell>
                        <TableCell align="center">
                          {report.reportEndDate != null ? (
                            <>
                              <Tooltip
                                title="View Report"
                                placement="top"
                              >
                                <span>
                                  <IconButton
                                    sx={{ padding: "4px" }}
                                    disabled={report.status === "Failed"}
                                    onClick={(e: any) =>
                                      getReport(
                                        e,
                                        report?.runId ?? 0,
                                        report?.countryCode
                                      )
                                    }
                                  >
                                    <VisibilityIcon />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip
                                title="Download Report"
                                placement="top"
                              >
                                <span>
                                  <IconButton
                                    sx={{ padding: "4px" }}
                                    disabled={report.status === "Failed"}
                                    onClick={(e: any) =>
                                      downloadreport(
                                        e,
                                        report?.runId ?? 0,
                                        report?.countryCode
                                      )
                                    }
                                  >
                                    <FileDownloadIcon />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </>
                          ) : (
                            <CircularProgress
                              size="30px"
                              disableShrink
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              )}
            </Table>
          </TableContainer>
        </Paper>

        <footer
          className="footer"
          style={{ position: "fixed", bottom: "1rem" }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              aliginItems: "center",
              alignContent: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                gap: 1,
                mt: "6px",
                mr: "1rem",
              }}
              alignItems={"center"}
            >
              <Typography sx={{ fontSize: "small" }}>
                Reports Per Page
              </Typography>

              <FormControl variant="standard">
                <Select
                  disableUnderline={true}
                  value={rows}
                  label="Reports Per Page"
                  onChange={handleChange}
                  sx={{
                    fontSize: "13px",
                  }}
                >
                  {[5, 10, 15].map((value, index) => (
                    <MenuItem
                      value={value}
                      key={value + String(index)}
                    >
                      {value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography sx={{ color: "#191F1C" }}>
                {indexOfLastReport > 0 ? indexOfFirstReport + 1 : 0}-
                {indexOfFirstReport + Number(rows) > reportRecords.length
                  ? reportRecords.length
                  : indexOfFirstReport + Number(rows)}{" "}
                out of {reportRecords.length}
              </Typography>
            </Box>
            <Pagination
              rows={rows}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalDataLength={reportRecords.length}
              dataPerPage={reportsPerPage}
            />
          </Box>
        </footer>
        <ToastContainer position="bottom-left" />
      </Container>
    </>
  );
};

export default ReportsScreen;
